
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { UserProvider, useUser } from "./contexts/UserContext";
import Index from "./pages/Index";
import Auth from "./pages/auth";
import Guards from "./pages/guards";
import Clients from "./pages/clients";
import Teams from "./pages/teams";
import TeamDetail from "./pages/teams/[teamId]"; 
import Reports from "./pages/reports";
import TeamReport from "./pages/reports/[teamId]";
import TeamMissions from "./pages/missions/[teamId]";
import Missions from "./pages/missions";
import Applications from "./pages/applications";
import TeamApplication from "./pages/applications/[teamId]";
import TeamBlog from "./pages/applications/components/TeamBlog";
import Users from "./pages/users";
import UserCredentials from "./pages/user-credentials";
import Settings from "./pages/settings";
import GuardProfile from "./pages/GuardProfile";
import NotFound from "./pages/NotFound";
import AdminTeamMembers from "./pages/AdminTeamMembers";
import Alerts from "./pages/alerts";
import Scheduling from "./pages/scheduling";
import ScheduleLogs from "./pages/schedule-logs";
import ScheduleAlerts from "./pages/schedule-alerts";
import GuardTeams from "./pages/guard-teams";
import GuardTeamSchedule from "./pages/guardteamschedule"; 
import Leave from "./pages/Leave";
import Equipment from "./pages/equipment";
import EnhancedDashboard from "./pages/EnhancedDashboard";
import HRDashboard from "./pages/hr/HRDashboard";
import DemographicsPage from "./pages/hr/DemographicsPage";
import CandidateTestPage from "./pages/hr/CandidateTestPage";
import TestResultsPage from "./pages/hr/TestResultsPage";
import TestResultDetailsPage from "./pages/hr/TestResultDetailsPage";
import CandidateWorkflowPage from "./pages/hr/CandidateWorkflowPage";
import PendingInterviews from "./pages/hr/PendingInterviews";
import DocumentationPage from "./pages/documentation/DocumentationPage";
import PublicDocumentationPage from "./pages/documentation/PublicDocumentationPage";
import Pricing from "./pages/Pricing";
import PricingDemo from "./pages/PricingDemo";
import TrainingDashboard from "@/pages/training/index";
import { TrainingAdminPanel } from "@/pages/training/components/TrainingAdminPanel";
import TrainingModule from "@/pages/training/TrainingModule";
import ScheduledSessions from "@/pages/training/ScheduledSessions";
import SessionParticipants from "@/pages/training/SessionParticipants";
import SessionReport from "@/pages/training/SessionReport";
import CertificatesPage from "@/pages/training/CertificatesPage";
import { TrainingAnalytics } from "@/pages/training/TrainingAnalytics";
import { TeamManagement } from "@/pages/training/TeamManagement";
import TrainingProgressDashboard from "@/pages/admin/TrainingProgressDashboard";
import ContentGeneration from "@/pages/admin/ContentGeneration";
import { AutoContentGeneration } from "@/pages/admin/AutoContentGeneration";
import AdminDashboard from "@/pages/admin-dashboard/[adminId]";
import PerformancePage from "@/pages/performance/PerformancePage";
import BehaviorTrackingPage from "@/pages/performance/BehaviorTrackingPage";
import ProviderEvaluationsPage from "@/pages/provider-evaluations/index";
import EthicsComplianceProtected from "@/pages/EthicsComplianceProtected";
import { useEffect, useState } from "react";
import { supabase } from "./integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useUserRoles } from "./hooks/useUserRoles";
import { useClientTeams } from "./hooks/useClientTeams";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { BrandingProvider } from "./components/BrandingProvider";
import AppLayout from "./components/layout/AppLayout";
import { PrivacyPolicyConsent } from "./components/PrivacyPolicyConsent";
import { PolicyAcknowledgementDialogAuto } from "./components/PolicyAcknowledgementDialogAuto";

// Detect docs mode at module level - before any component rendering
const IS_DOCS_MODE = (() => {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  const href = window.location.href;
  const pathname = window.location.pathname;
  
  const isDocsMode = 
    hostname === 'docs.shiftask.app' || 
    hostname.startsWith('docs.') ||
    href.includes('docs.shiftask.app') ||
    pathname === '/public-documentation';
    
  console.log('=== MODULE LEVEL DOCS DETECTION ===');
  console.log('hostname:', hostname);
  console.log('href:', href);
  console.log('pathname:', pathname);
  console.log('IS_DOCS_MODE:', isDocsMode);
  console.log('=====================================');
  
  return isDocsMode;
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        if (error.message?.includes('ERR_INSUFFICIENT_RESOURCES')) {
          console.warn('Resource limitation error detected, not retrying');
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 300000, // 5 minutes
      gcTime: 600000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  },
});

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { user, isLoading } = useUser();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/auth");
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-foreground">Loading...</div>
      </div>
    );
  }

  return user ? <>{children}</> : null;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAdmin, isLoading } = useUserRoles();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [directAdminCheck, setDirectAdminCheck] = useState<boolean | null>(null);
  
  useEffect(() => {
    const checkAdminDirectly = async () => {
      try {
        const { data: isAdminResult, error } = await supabase.rpc('is_admin', {
          user_id: (await supabase.auth.getUser()).data.user?.id
        });
        
        if (error) {
          console.error('Error checking admin status directly:', error);
          setDirectAdminCheck(false);
        } else {
          console.log('Direct admin check result:', isAdminResult);
          setDirectAdminCheck(!!isAdminResult);
        }
      } catch (err) {
        console.error('Error in direct admin check:', err);
        setDirectAdminCheck(false);
      }
    };
    
    checkAdminDirectly();
  }, []);
  
  useEffect(() => {
    if (!isLoading && directAdminCheck !== null && !isAdmin && !directAdminCheck) {
      console.log('AdminRoute: Non-admin user attempted to access admin route');
      navigate('/');
    }
  }, [isAdmin, isLoading, navigate, directAdminCheck]);
  
  if (isLoading || directAdminCheck === null) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-foreground text-lg">{t('loading')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  return (isAdmin || directAdminCheck) ? <>{children}</> : null;
};

const AdminOrManagerRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAdmin, isManager, isLoading } = useUserRoles();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [directAdminCheck, setDirectAdminCheck] = useState<boolean | null>(null);
  const [directManagerCheck, setDirectManagerCheck] = useState<boolean | null>(null);
  
  useEffect(() => {
    const checkRolesDirectly = async () => {
      try {
        const userId = (await supabase.auth.getUser()).data.user?.id;
        
        const { data: isAdminResult, error: adminError } = await supabase.rpc('is_admin', {
          user_id: userId
        });
        
        const { data: isManagerResult, error: managerError } = await supabase.rpc('is_manager', {
          user_id: userId
        });
        
        if (adminError) {
          console.error('Error checking admin status directly:', adminError);
          setDirectAdminCheck(false);
        } else {
          setDirectAdminCheck(!!isAdminResult);
        }

        if (managerError) {
          console.error('Error checking manager status directly:', managerError);
          setDirectManagerCheck(false);
        } else {
          setDirectManagerCheck(!!isManagerResult);
        }
      } catch (err) {
        console.error('Error in direct role checks:', err);
        setDirectAdminCheck(false);
        setDirectManagerCheck(false);
      }
    };
    
    checkRolesDirectly();
  }, []);
  
  useEffect(() => {
    if (!isLoading && directAdminCheck !== null && directManagerCheck !== null && 
        !isAdmin && !isManager && !directAdminCheck && !directManagerCheck) {
      console.log('AdminOrManagerRoute: User without admin or manager role attempted access');
      navigate('/');
    }
  }, [isAdmin, isManager, isLoading, navigate, directAdminCheck, directManagerCheck]);
  
  if (isLoading || directAdminCheck === null || directManagerCheck === null) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-foreground text-lg">{t('loading')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  return (isAdmin || isManager || directAdminCheck || directManagerCheck) ? <>{children}</> : null;
};

const AdminOrGuardRoute = ({ children, allowGuard = false }: { children: React.ReactNode, allowGuard?: boolean }) => {
  const { isAdmin, isLoading, roles } = useUserRoles();
  const { t } = useLanguage();
  const isGuard = roles?.includes('guard');
  
  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-foreground text-lg">{t('loading')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  return (isAdmin || (allowGuard && isGuard)) ? <>{children}</> : <Navigate to="/" />;
};

const GuardRoute = ({ children }: { children: React.ReactNode }) => {
  const { roles, isLoading } = useUserRoles();
  const { t } = useLanguage();
  const isGuard = roles?.includes('guard');
  
  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-foreground text-lg">{t('loading')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  return isGuard ? <>{children}</> : <Navigate to="/" />;
};

const AdminOrClientRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAdmin, isLoading } = useUserRoles();
  const { isClient, isLoading: clientLoading } = useClientTeams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [directAdminCheck, setDirectAdminCheck] = useState<boolean | null>(null);
  const [directClientCheck, setDirectClientCheck] = useState<boolean | null>(null);
  
  useEffect(() => {
    const checkRolesDirectly = async () => {
      try {
        const userId = (await supabase.auth.getUser()).data.user?.id;
        
        const { data: isAdminResult, error: adminError } = await supabase.rpc('is_admin', {
          user_id: userId
        });
        
        const { data: clientTeams, error: clientError } = await supabase
          .from('client_teams')
          .select('team_id')
          .eq('client_id', userId);
        
        if (adminError) {
          console.error('Error checking admin status directly:', adminError);
          setDirectAdminCheck(false);
        } else {
          setDirectAdminCheck(!!isAdminResult);
        }

        if (clientError) {
          console.error('Error checking client status directly:', clientError);
          setDirectClientCheck(false);
        } else {
          setDirectClientCheck((clientTeams?.length || 0) > 0);
        }
      } catch (err) {
        console.error('Error in direct role checks:', err);
        setDirectAdminCheck(false);
        setDirectClientCheck(false);
      }
    };
    
    checkRolesDirectly();
  }, []);
  
  useEffect(() => {
    if (!isLoading && !clientLoading && directAdminCheck !== null && directClientCheck !== null && 
        !isAdmin && !isClient && !directAdminCheck && !directClientCheck) {
      console.log('AdminOrClientRoute: User without admin or client role attempted access');
      navigate('/');
    }
  }, [isAdmin, isClient, isLoading, clientLoading, navigate, directAdminCheck, directClientCheck]);
  
  if (isLoading || clientLoading || directAdminCheck === null || directClientCheck === null) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-foreground text-lg">{t('loading')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  return (isAdmin || isClient || directAdminCheck || directClientCheck) ? <>{children}</> : null;
};

const App = () => {
  // Use the module-level detection
  console.log('🔍 App component - IS_DOCS_MODE:', IS_DOCS_MODE);

  // FORCE docs mode - return immediately without any routing
  if (IS_DOCS_MODE) {
    console.log('🚀 RENDERING DOCS MODE');
    return (
      <ThemeProvider defaultTheme="light">
        <LanguageProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <PublicDocumentationPage />
            </TooltipProvider>
          </QueryClientProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  console.log('📱 RENDERING MAIN APP');

  return (
    <ThemeProvider defaultTheme="light">
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <LanguageProvider>
            <BrandingProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <PrivacyPolicyConsent />
                <PolicyAcknowledgementDialogAuto />
                <BrowserRouter>
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/" element={<PublicDocumentationPage />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/pricing-demo" element={<PricingDemo />} />
                <Route path="/dashboard" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                <Route path="/guards" element={<ProtectedRoute><Guards /></ProtectedRoute>} />
                <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
                <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
                <Route path="/teams/:teamId" element={<ProtectedRoute><TeamDetail /></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
                <Route path="/reports/team/:teamId" element={<ProtectedRoute><TeamReport /></ProtectedRoute>} />
                <Route path="/missions" element={<ProtectedRoute><Missions /></ProtectedRoute>} />
                <Route path="/missions/team/:teamId" element={<ProtectedRoute><TeamMissions /></ProtectedRoute>} />
                <Route path="/leave" element={<ProtectedRoute><Leave /></ProtectedRoute>} />
                <Route path="/performance" element={<ProtectedRoute><PerformancePage /></ProtectedRoute>} />
                <Route path="/performance/behaviors" element={<AdminRoute><BehaviorTrackingPage /></AdminRoute>} />
                <Route path="/provider-evaluations" element={<ProtectedRoute><ProviderEvaluationsPage /></ProtectedRoute>} />
                <Route path="/applications" element={<ProtectedRoute><Applications /></ProtectedRoute>} />
                <Route path="/applications/team/:teamId" element={<ProtectedRoute><TeamApplication /></ProtectedRoute>} />
                <Route path="/applications/team/:teamId/chat" element={<ProtectedRoute><TeamBlog /></ProtectedRoute>} />
                <Route path="/users" element={<AdminOrManagerRoute><Users /></AdminOrManagerRoute>} />
                <Route path="/user-credentials" element={<AdminRoute><UserCredentials /></AdminRoute>} />
                <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
                <Route path="/profile" element={<ProtectedRoute><GuardProfile /></ProtectedRoute>} />
                <Route path="/admin-team" element={<AdminOrManagerRoute><AdminTeamMembers /></AdminOrManagerRoute>} />
                <Route path="/scheduling" element={<ProtectedRoute><Scheduling /></ProtectedRoute>} />
                <Route path="/schedule-logs" element={<ProtectedRoute><ScheduleLogs /></ProtectedRoute>} />
                <Route path="/schedule-alerts" element={<ProtectedRoute><ScheduleAlerts /></ProtectedRoute>} />
                <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
                <Route path="/guard-teams" element={<ProtectedRoute><GuardTeams /></ProtectedRoute>} />
                <Route path="/guardteamschedule" element={<ProtectedRoute><GuardTeamSchedule /></ProtectedRoute>} />
                <Route path="/guajdteamtest" element={<Navigate to="/guardteamschedule" replace />} />
                <Route path="/equipment" element={<AdminRoute><Equipment /></AdminRoute>} />
                <Route path="/ethics-compliance" element={<ProtectedRoute><EthicsComplianceProtected /></ProtectedRoute>} />
                <Route path="/enhanced-dashboard" element={<AdminRoute><EnhancedDashboard /></AdminRoute>} />
                <Route path="/hr" element={<AdminRoute><HRDashboard /></AdminRoute>} />
                <Route path="/hr/demographics" element={<AdminOrClientRoute><DemographicsPage /></AdminOrClientRoute>} />
                <Route path="/hr/candidate/:candidateId" element={<AdminRoute><CandidateWorkflowPage /></AdminRoute>} />
                <Route path="/hr/candidate-test/:candidateId" element={<AdminRoute><CandidateTestPage /></AdminRoute>} />
                <Route path="/hr/test-results/:candidateId" element={<AdminRoute><TestResultsPage /></AdminRoute>} />
                <Route path="/hr/pending-interviews" element={<AdminRoute><PendingInterviews /></AdminRoute>} />
                <Route path="/hr/test-details/:resultId" element={<AdminRoute><TestResultDetailsPage /></AdminRoute>} />
                <Route path="/training" element={<ProtectedRoute><TrainingDashboard /></ProtectedRoute>} />
                <Route path="/training/admin" element={<AdminRoute><TrainingAdminPanel /></AdminRoute>} />
                <Route path="/training/module/:id" element={<ProtectedRoute><TrainingModule /></ProtectedRoute>} />
                <Route path="/training/sessions" element={<ProtectedRoute><ScheduledSessions /></ProtectedRoute>} />
                <Route path="/training/sessions/:sessionId/participants" element={<ProtectedRoute><SessionParticipants /></ProtectedRoute>} />
                <Route path="/training/sessions/:sessionId/report" element={<ProtectedRoute><SessionReport /></ProtectedRoute>} />
                <Route path="/training/certificates" element={<ProtectedRoute><CertificatesPage /></ProtectedRoute>} />
                <Route path="/training/analytics" element={<ProtectedRoute><TrainingAnalytics /></ProtectedRoute>} />
                <Route path="/admin/training-progress" element={<AdminRoute><TrainingProgressDashboard /></AdminRoute>} />
                <Route path="/admin/content-generation" element={<AdminRoute><ContentGeneration /></AdminRoute>} />
                <Route path="/admin/:adminId" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="/admin/auto-content-generation" element={<AdminRoute><AutoContentGeneration /></AdminRoute>} />
                <Route path="/documentation" element={<AdminRoute><DocumentationPage /></AdminRoute>} />
                <Route path="/public-documentation" element={<PublicDocumentationPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
          </BrandingProvider>
        </LanguageProvider>
        </UserProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
